/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ILxs2ColumnContentBlockAdditionalContentItemViewProps,
    ILxs2ColumnContentBlockAdditionalContentViewProps,
    ILxs2ColumnContentBlockViewProps
} from './lxs-2-column-content-block.data';

const renderAdditionalContent = (additionalContent: ILxs2ColumnContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: ILxs2ColumnContentBlockAdditionalContentItemViewProps, index: number) => {
                return (
                    <React.Fragment key={index}>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </React.Fragment>
                );
            })}
        </Node>
    );
};

const renderImage = (props: ILxs2ColumnContentBlockViewProps): React.ReactNode => {
    const { imageContainer, image, contentBlockAnchorTag, imageLink, imageAriaLabel } = props;

    if (!image) {
        return null;
    }

    if (imageLink) {
        return (
            <Node
                {...contentBlockAnchorTag}
                href={imageLink}
                className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                aria-label={imageAriaLabel}
            >
                <Node {...imageContainer}>{image}</Node>
            </Node>
        );
    }

    return <Node {...imageContainer}>{image}</Node>;
};

const Lxs2ColumnContentBlockView: React.FC<ILxs2ColumnContentBlockViewProps> = props => {
    const { contentBlockContainer, innerContainer, detailsContainer, preheading, title, text, links, ctaButton, additionalContent } = props;

    return (
        <Module {...contentBlockContainer}>
            <Node {...innerContainer}>
                {renderImage(props)}
                <Node {...detailsContainer}>
                    {preheading}
                    {title}
                    {text}
                    {links}
                    {ctaButton}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Node>
        </Module>
    );
};

export default Lxs2ColumnContentBlockView;
